import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

// Record an SOS event when user presses the SOS button
export const recordSOSEvent = async () => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    
    if (!userId) {
      console.error('User not authenticated');
      return false;
    }
    
    const { error } = await supabase
      .from('sos_events')
      .insert([{ user_id: userId }]);
    
    if (error) {
      console.error('Error recording SOS event:', error);
      toast({
        title: "Error",
        description: "Failed to record SOS event.",
        variant: "destructive",
      });
      return false;
    }
    
    toast({
      title: "SOS recorded",
      description: "Your SOS event has been recorded.",
    });
    
    return true;
  } catch (error) {
    console.error('Error in recordSOSEvent:', error);
    return false;
  }
};

// Start a game session when user begins playing
export const startGameSession = async (gameId: number, gameTitle: string) => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    
    if (!userId) {
      console.error('User not authenticated');
      return null;
    }
    
    const { data, error } = await supabase
      .from('game_sessions')
      .insert([{ 
        user_id: userId,
        game_id: gameId,
        game_title: gameTitle,
      }])
      .select();
    
    if (error) {
      console.error('Error starting game session:', error);
      // Only show errors for authenticated users
      toast({
        title: "Error",
        description: "Failed to start game session.",
        variant: "destructive",
      });
      return null;
    }
    
    return data?.[0];
  } catch (error) {
    console.error('Error in startGameSession:', error);
    return null;
  }
};

// Complete a game session when user finishes or exits the game
export const completeGameSession = async (sessionId: string, durationSeconds: number) => {
  try {
    const { error } = await supabase
      .from('game_sessions')
      .update({ 
        completed_at: new Date().toISOString(),
        duration_seconds: durationSeconds 
      })
      .eq('id', sessionId);
    
    if (error) {
      console.error('Error completing game session:', error);
      // Error toast will be handled by calling component based on authentication status
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error in completeGameSession:', error);
    // Error toast will be handled by calling component based on authentication status
    return false;
  }
};

// Get user's game history
export const getUserGameHistory = async () => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    
    if (!userId) {
      console.error('User not authenticated');
      return [];
    }
    
    const { data, error } = await supabase
      .from('game_sessions')
      .select('*')
      .eq('user_id', userId)
      .order('started_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching game history:', error);
      toast({
        title: "Error",
        description: "Failed to fetch game history.",
        variant: "destructive",
      });
      return [];
    }
    
    return data || [];
  } catch (error) {
    console.error('Error in getUserGameHistory:', error);
    return [];
  }
};

// Get user's SOS event history
export const getUserSOSEvents = async () => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    
    if (!userId) {
      console.error('User not authenticated');
      return [];
    }
    
    const { data, error } = await supabase
      .from('sos_events')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching SOS events:', error);
      toast({
        title: "Error",
        description: "Failed to fetch SOS events.",
        variant: "destructive",
      });
      return [];
    }
    
    return data || [];
  } catch (error) {
    console.error('Error in getUserSOSEvents:', error);
    return [];
  }
};

// Get stats about user's game usage
export const getUserStats = async () => {
  try {
    const gameHistory = await getUserGameHistory();
    const sosEvents = await getUserSOSEvents();
    
    const totalGamesPlayed = gameHistory.length;
    const totalCompletedGames = gameHistory.filter(game => game.completed_at).length;
    const totalSOSEvents = sosEvents.length;
    const averageDuration = gameHistory
      .filter(game => game.duration_seconds)
      .reduce((sum, game) => sum + (game.duration_seconds || 0), 0) / totalCompletedGames || 0;
    
    // Group by day for the last 7 days
    const now = new Date();
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      return date.toISOString().split('T')[0];
    }).reverse();
    
    const gamesByDay = last7Days.map(day => {
      return {
        day,
        count: gameHistory.filter(game => game.started_at && game.started_at.startsWith(day)).length
      };
    });
    
    const sosByDay = last7Days.map(day => {
      return {
        day,
        count: sosEvents.filter(event => event.created_at && event.created_at.startsWith(day)).length
      };
    });
    
    return {
      totalGamesPlayed,
      totalCompletedGames,
      totalSOSEvents,
      completionRate: totalGamesPlayed > 0 ? (totalCompletedGames / totalGamesPlayed) * 100 : 0,
      averageDuration,
      gamesByDay,
      sosByDay
    };
  } catch (error) {
    console.error('Error in getUserStats:', error);
    return {
      totalGamesPlayed: 0,
      totalCompletedGames: 0,
      totalSOSEvents: 0,
      completionRate: 0,
      averageDuration: 0,
      gamesByDay: [],
      sosByDay: []
    };
  }
};
